'use client';

import { CARD_LABELS } from '@/constants/labels';
import { CertificationProgress, Icon, RoleplayCardProps, TextContent } from '@/components';
import { CertificationSimulations } from '../CertificationSimulations';
import { cn } from '@/utils';
import { ICertification } from '@/types';
import { useAnimatedHeightTransition } from '@/hooks';
import { useResponsive } from '@/hooks';
import React, { useState } from 'react';

export interface CertificationProps {
  /**
   * The certification data to display.
   */
  certification?: ICertification;
  /**
   * Whether the certification section is collapsible.
   */
  isCollapsible?: boolean;
  /**
   * Whether the certification section is collapsed.
   */
  isCollapsed?: boolean;
  /**
   * Loading state indicator
   */
  isLoading?: boolean;
  /**
   * Callback for sharing a certification with the manager
   */
  onShareWithManager: RoleplayCardProps['onShareWithManager'];
  /**
   * Callback for viewing the scorecard of a certification
   */
  onViewScoreCard: RoleplayCardProps['onViewScoreCard'];
  /**
   * Whether to show the category section.
   */
  showCategory?: boolean;
  /**
   * Whether to show the progress section.
   */
  showProgress?: boolean;
  /**
   * Whether to show the simulations section.
   */
  showSimulations?: boolean;
  /**
   * Optional user ID for context in actions like
   * sharing with manager or viewing scorecards.
   */
  userId?: string;
}

export const Certification: React.FC<CertificationProps> = ({
  certification,
  isCollapsed = false,
  isCollapsible = false,
  isLoading = false,
  onShareWithManager,
  onViewScoreCard,
  showCategory = false,
  showProgress = false,
  showSimulations = true,
  userId,
}) => {
  const [isExpanded, setIsExpanded] = useState(!isCollapsed ? true : false);
  const isXlUp = useResponsive({ breakpoint: 'xl', direction: 'min' });
  const {
    contentRef,
    height,
    handleTransitionEnd,
    isAnimating,
    prepareForTransition,
  } = useAnimatedHeightTransition({
    dependencies: [
      certification?.category,
      certification?.description,
      certification?.progress,
      certification?.simulations?.length,
      certification?.subtitle,
      certification?.uuid,
      isExpanded,
      isLoading,
      showCategory,
      showProgress,
      showSimulations,
    ],
    getCurrentHeight: (element) => {
      if (!isCollapsible) {
        return element?.getBoundingClientRect().height;
      }

      return isExpanded ? element?.getBoundingClientRect().height : 0;
    },
    getNextHeight: (element) => {
      if (!isCollapsible) {
        return element?.getBoundingClientRect().height;
      }

      return isExpanded ? element?.getBoundingClientRect().height : 0;
    },
  });

  const toggleCollapse = () => {
    prepareForTransition();
    setIsExpanded(!isExpanded);
  };

  const detailsWrapperStyle = !isCollapsible
    ? undefined
    : typeof height === 'number'
      ? { height: `${height}px` }
      : isExpanded
        ? undefined
        : { height: '0px' };

  return (
    <section
      aria-label={`Certification: ${certification?.title}`}
      className="rounded-lg bg-neutral-white"
      data-content="Certification"
      data-testid={`certification-${certification?.uuid}`}
      id={`certification-${certification?.uuid}`}
      role="region"
    >
      {isCollapsible ? (
        <button
          aria-controls={`certification-content-${certification?.uuid}`}
          aria-expanded={isExpanded}
          aria-label="Toggle Certification Details"
          className="flex w-full items-start justify-between p-6 text-start text-semantic-text-primary hover:text-semantic-text-secondary md:items-center"
          data-content="Certification Title Button"
          data-testid={`certification-${certification?.uuid}-toggle-button`}
          disabled={isLoading}
          onClick={toggleCollapse}
        >
          <TextContent
            dataContent="Certification Title"
            isLoading={isLoading}
            label={certification?.title}
            level={2}
            loadingClassName="h-[2rem]"
            testId={`certification-${certification?.uuid}-title`}
            type="headline"
          />
          {isExpanded ? <Icon name="CaretUp" size={24} /> : <Icon name="CaretDown" size={24} />}
        </button>
      ) : (
        <TextContent
          dataContent="Certification Title"
          isLoading={isLoading}
          label={certification?.title}
          level={2}
          loadingClassName="h-[2rem]"
          testId={`certification-${certification?.uuid}-title`}
          type="headline"
        />
      )}

      <div
        aria-hidden={isCollapsible ? !isExpanded : undefined}
        className={cn(
          'overflow-hidden transition-[height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
          {
            'pointer-events-none': isCollapsible && !isExpanded,
          },
        )}
        data-content="Expanded Content"
        data-testid={`certification-${certification?.uuid}-content-wrapper`}
        id={`certification-${certification?.uuid}-content`}
        onTransitionEnd={handleTransitionEnd}
        style={detailsWrapperStyle}
      >
        <div
          className={cn('transition-opacity duration-200 ease-out motion-reduce:transition-none', {
            'animate-fade-in-down animate-duration-fast': isExpanded,
            'opacity-95': isAnimating,
          })}
          ref={contentRef}
        >
          <div className="flex flex-col p-6 pb-0 pt-0">
            <TextContent
              dataContent="Certification Subtitle"
              isLoading={isLoading}
              label={certification?.subtitle || CARD_LABELS.ROLEPLAY_PRACTICE_SCENARIOS}
              loadingClassName="h-5 w-1/2"
              testId={`certification-${certification?.uuid}-subtitle`}
              type="paragraph"
            />

            {showCategory && certification?.category && (
              <TextContent
                className="mt-2 underline decoration-solid decoration-from-font underline-offset-4"
                dataContent="Certification Category"
                isLoading={isLoading}
                label={certification?.category}
                loadingClassName="h-2 w-1/2"
                testId={`certification-${certification?.uuid}-category`}
                type="paragraph"
                variant="bodySmall"
              />
            )}

            {certification?.description && (
              <TextContent
                className="mt-4"
                color="neutral-gray-700"
                dataContent="Certification Description"
                isLoading={isLoading}
                label={certification?.description}
                loadingClassName="w-full mb-1 h-4"
                loadingLines={isXlUp ? 2 : 3}
                testId={`certification-${certification?.uuid}-description`}
                type="paragraph"
                variant="bodyMedium"
              />
            )}
          </div>

          {showProgress && (
            <div
              className="p-6"
              data-content="Certification Progress"
              data-testid={`certification-${certification?.uuid}-progress`}
            >
              <CertificationProgress
                progress={certification?.progress || 0}
                simulations={certification?.simulations || []}
                uuid={certification?.uuid || ''}
              />
            </div>
          )}

          {showSimulations && (
            <div
              data-content="Certification Simulations Container"
              data-testid={`certification-${certification?.uuid}-simulations-container`}
              id={`certification-${certification?.uuid}-simulations-container`}
            >
              {certification && (
                <CertificationSimulations
                  certification={certification}
                  certificationPractice={certification?.certificationPractice}
                  certificationSimulation={certification?.certificationSimulation}
                  isLoading={isLoading}
                  onShareWithManager={onShareWithManager}
                  onViewScoreCard={onViewScoreCard}
                  simulations={certification?.simulations || []}
                  userId={userId}
                  uuid={certification?.uuid || ''}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
